import { useMemo, useState } from "react";
import { bool, object, number } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { Tables, hashCode, BreakdownTableDepths, DescriptionModes } from "@repo/utils";
import { BREAKDOWN_ELEMENT_CODES, CELL_WIDTHS } from "@utils";
import {
  useEstimateStructure,
  useEstimateEditorSettings,
  useEstimateTableSettings,
  useTableExpandHandler,
  PinnableCell,
  Row,
  RowGroup,
} from "@tools";
import { WysiwygEditor } from "@components";
import { Collapse } from "@material-ui/core";
import { BudgetTrackingTaskWorkType } from "./BudgetTrackingTaskWorkType";
import { BudgetTrackingBreakdownRowGroupList } from "./BudgetTrackingBreakdownRowGroupList";
import classnames from "classnames";

export const BudgetTrackingBreakdownRowGroup = observer(({
  element,
  isLastGroupRow,
  tableDepth = 0,
  initialDepth = 0,
}) => {
  const {
    allowEdition,
    useVisibilityCell,
    blockExpansions,
    displayLibraryElementStatus,
  } = useEstimateEditorSettings();
  const { useExpandCell } = useEstimateTableSettings();
  const structure = useEstimateStructure();
  const { t } = useTranslation();

  const [descEdition] = useState(undefined);

  const { valueLevel, viewLevel, descriptionMode } = structure.settings;

  const {
    id: elementId,
    treePath,
    name,
    content,
    isTurnedOff,
    hasChildren,
    hasFixedPrice,
    isOpened,
    status,
    showMoreContent,
  } = element;

  const actualDepthLevel = useMemo(
    () => tableDepth + initialDepth,
    [tableDepth, initialDepth]
  );

  const openable = useMemo(
    () => actualDepthLevel < viewLevel && (allowEdition || hasChildren),
    [actualDepthLevel, viewLevel, allowEdition, hasChildren]
  );

  const hasEditedDescription = useMemo(
    () =>
      descriptionMode === DescriptionModes.ROW ? Boolean(content) || descEdition : descEdition,
    [descEdition, descriptionMode, content]
  );

  const isAcceptedComponent = useMemo(
    () => status === 1 && displayLibraryElementStatus,
    [status, displayLibraryElementStatus]
  );

  const opened = useMemo(
    () => isAcceptedComponent || (openable && (isOpened || blockExpansions)),
    [isAcceptedComponent, openable, isOpened, blockExpansions]
  );

  const isSectionRow = useMemo(
    () => actualDepthLevel === BreakdownTableDepths.SECTION,
    [actualDepthLevel]
  );

  const displayCellContent = useMemo(
    () =>
      actualDepthLevel <= valueLevel &&
      (!opened || !hasChildren || tableDepth + 1 > valueLevel),
    [actualDepthLevel, valueLevel, opened, hasChildren, tableDepth]
  );
  
  const hashedPath = useMemo(() => (
    [Tables.BREAKDOWN, ...treePath].map((id) => hashCode(id))
  ), [treePath]);

  const handleTurnOff = () => {
    element.setTurnOffState(!isTurnedOff, true);
  };

  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: openable,
    isOpened: isOpened,
    onExpand: element.setOpenState
  });

  return (
    <RowGroup
      tableDepth={tableDepth}
      isActive={opened}
      isStatic={!openable}
      hashedPath={hashedPath}
      elementType={BREAKDOWN_ELEMENT_CODES[actualDepthLevel]}
    >
      <Row
        useExpandCell={useExpandCell}
        expandable={openable}
        expanded={opened}
        onExpand={handleOpen}
        noArrow={actualDepthLevel === BreakdownTableDepths.MODULE}
        expandOnClick
        useVisibilityCell={useVisibilityCell}
        visible={!isTurnedOff}
        onVisibilityToggle={handleTurnOff}
        isFirstGroupRow={tableDepth <= 1}
        isLastGroupRow={ isSectionRow || (isLastGroupRow && !opened && !hasEditedDescription) }
        highlightCellText={isAcceptedComponent}
        tableDepth={tableDepth}
        style={!tableDepth ? { marginBottom: 2 } : undefined}
      >
        <PinnableCell
          allowOverflowDisplay={descriptionMode < DescriptionModes.COLUMN}
          minWidth={CELL_WIDTHS.TITLE_BT_SUMMARY}
          maxWidth={CELL_WIDTHS.DESCRIPTION}
          pinnedLeft={useExpandCell ? tableDepth || 1 : 0}
        >
          {
            <span
              className={classnames(
                "name wrap-text my-3",
                !isSectionRow && "preset-titleText",
                isSectionRow && "preset-titleTextSection",
                !name?.length && "semi-transparent",
                !useExpandCell && "ml-3"
              )}
            >
              {name?.replace(/<(.|\n)*?>/g, "") || t("common.unnamed")}
            </span>
          }
        </PinnableCell>
        <PinnableCell
          allowOverflowDisplay
          minWidth={CELL_WIDTHS.TITLE_BT_SUMMARY}
        >
          <WysiwygEditor
            readOnly
            noEmptyHtmlStrings
            changeOnClickAway
            placeholder={t("views.editor.desc_change")}
            name={`desc${hashCode(elementId)}`}
            value={content}
            pinToolbarToContainer
            shorten
            useBorder
            showMore={showMoreContent}
            onShowMore={element.showMoreContent}
            moreButtonClassName={classnames(
              !isSectionRow && "preset-moreDesc",
              isSectionRow && "preset-moreDescSection color-primary-lighter"
            )}
            className={allowEdition ? "my-1-5" : "my-3 ml-0-5"}
          />
        </PinnableCell>
        {
          element.btTaskWorkTypes?.map((workType) => (
          <BudgetTrackingTaskWorkType
            key={workType.id}
            tableDepth={tableDepth}
            element={element}
            workType={workType}
            editable={ allowEdition }
            displayContent={displayCellContent}
            emptyValue={hasFixedPrice}
          />
        ))
        }
      </Row>
      {openable && (
        <Collapse
          in={opened}
          className="w-full"
          mountOnEnter
          unmountOnExit
          data-id={hashCode(`${elementId}_cont`)}
          data-p={hashedPath.join("/")}
          data-t={BREAKDOWN_ELEMENT_CODES[actualDepthLevel + 1]}
          {...collapseProps}
        >
          <BudgetTrackingBreakdownRowGroupList
            element={element}
            tableDepth={tableDepth}
            initialDepth={initialDepth}
            isLastGroupRow={ tableDepth === 0 || (isLastGroupRow && !allowEdition) }
            className="w-full"
          />
        </Collapse>
      )}
    </RowGroup>
  );
});

BudgetTrackingBreakdownRowGroup.propTypes = {
  element: object.isRequired,
  tableDepth: number,
  initialDepth: number,
  isLastGroupRow: bool,
};
